import { useEffect, useId, useState, type SyntheticEvent } from 'react';
import { Link } from 'react-router';

import {
  AuthField,
  authInputClass,
  AuthLayout,
  AuthNotice,
  AuthSubmit,
} from '../features/auth/AuthLayout';
import {
  acceptInvite,
  inspectInvite,
  sendInviteCode,
  type Invitation,
} from '../features/auth/session';
import { rememberTenant } from '../features/auth/tenantMemory';
import { ApiError, NetworkError } from '../lib/api';

const MIN_LENGTH = 12;

function readToken(): string {
  return new URLSearchParams(window.location.hash.slice(1)).get('token') ?? '';
}

type Stage = 'loading' | 'invalid' | 'error' | 'code' | 'password' | 'done';

function problem(err: unknown): string {
  if (err instanceof NetworkError)
    return 'Não foi possível contatar o servidor. Verifique sua conexão.';
  if (err instanceof ApiError && err.status === 429)
    return 'Muitos códigos pedidos. Aguarde e tente novamente mais tarde.';
  return 'Ocorreu um erro inesperado. Tente novamente.';
}

/**
 * Accepts an e-mailed invitation (ADR-030): confirms a code sent to the same
 * address, then sets the first password.
 */
export function AcceptInvitePage() {
  const [token] = useState(readToken);
  const [invite, setInvite] = useState<Invitation | null>(null);
  const [stage, setStage] = useState<Stage>(token ? 'loading' : 'invalid');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const errorId = useId();

  useEffect(() => {
    // The token stays out of the address bar (and of history) once read.
    if (window.location.hash) window.history.replaceState(null, '', window.location.pathname);
    if (!token) return;
    let active = true;
    inspectInvite(token).then(
      (i) => {
        if (!active) return;
        setInvite(i);
        setStage('code');
      },
      (err: unknown) => {
        if (!active) return;
        setStage(err instanceof ApiError && err.status === 422 ? 'invalid' : 'error');
      },
    );
    return () => {
      active = false;
    };
  }, [token]);

  const askCode = async () => {
    setError(null);
    setBusy(true);
    try {
      await sendInviteCode(token);
      setNotice(`Enviamos um código de 6 dígitos para ${invite?.email ?? 'o seu e-mail'}.`);
      setStage('password');
    } catch (err) {
      if (err instanceof ApiError && err.status === 422) setStage('invalid');
      else setError(problem(err));
    } finally {
      setBusy(false);
    }
  };

  const submit = async (e: SyntheticEvent) => {
    e.preventDefault();
    if (busy) return;
    setError(null);
    if (password.length < MIN_LENGTH) {
      setError(`A senha deve ter pelo menos ${String(MIN_LENGTH)} caracteres.`);
      return;
    }
    if (password !== confirm) {
      setError('As duas senhas não são iguais.');
      return;
    }
    setBusy(true);
    try {
      const done = await acceptInvite(token, code, password);
      rememberTenant(done.tenant_slug);
      setInvite(done);
      setStage('done');
    } catch (err) {
      const f = err instanceof ApiError ? err.fields : {};
      if (f.token) setStage('invalid');
      else if (f.code === 'incorrect')
        setError('Código incorreto. Confira o último e-mail recebido.');
      else if (f.code === 'expired')
        setError('O código expirou ou teve tentativas demais. Peça um novo código.');
      else if (f.new_password)
        setError(`A senha deve ter pelo menos ${String(MIN_LENGTH)} caracteres.`);
      else setError(problem(err));
    } finally {
      setBusy(false);
    }
  };

  if (stage === 'loading')
    return (
      <AuthLayout title="Convite" subtitle="Abrindo o seu convite…">
        <div className="mt-5 h-20 animate-pulse rounded-md bg-surface-hover" aria-hidden="true" />
      </AuthLayout>
    );

  if (stage === 'invalid' || stage === 'error')
    return (
      <AuthLayout
        title={stage === 'invalid' ? 'Convite inválido' : 'Não foi possível abrir o convite'}
        subtitle={
          stage === 'invalid' ? 'Este convite não pode mais ser usado.' : 'Tente novamente.'
        }
      >
        <div className="mt-5 space-y-4">
          <AuthNotice tone="error">
            {stage === 'invalid'
              ? 'O convite expirou, já foi aceito, foi substituído por um mais novo ou o link está incompleto. Peça ao administrador da sua organização um novo convite.'
              : 'Verifique sua conexão e abra o link do e-mail novamente.'}
          </AuthNotice>
          <p className="text-center text-[13px]">
            <Link to="/login" className="text-accent hover:underline">
              Ir para o login
            </Link>
          </p>
        </div>
      </AuthLayout>
    );

  if (stage === 'done' && invite)
    return (
      <AuthLayout title="Acesso criado" subtitle="Você já pode entrar no Pro Guard.">
        <div className="mt-5 space-y-4">
          <AuthNotice tone="success">
            Pronto, {invite.display_name}. Para entrar, use a organização{' '}
            <strong className="font-mono">{invite.tenant_slug}</strong>, o e-mail{' '}
            <strong>{invite.email}</strong> e a senha que você acabou de criar.
          </AuthNotice>
          <Link
            to="/login"
            className="inline-flex h-9 w-full items-center justify-center rounded-md bg-accent-strong text-[13px] font-medium text-white hover:bg-accent"
          >
            Ir para o login
          </Link>
        </div>
      </AuthLayout>
    );

  return (
    <AuthLayout
      title={`Olá, ${invite?.display_name ?? ''}`}
      subtitle={`Você foi convidado para usar o Pro Guard em ${invite?.tenant_name ?? ''}.`}
    >
      <div className="mt-5 space-y-4">
        <ol className="space-y-1 text-[13px] text-fg-muted">
          <li className={stage === 'code' ? 'text-fg' : ''}>
            1. Confirme que o e-mail <strong className="text-fg">{invite?.email}</strong> é seu.
          </li>
          <li className={stage === 'password' ? 'text-fg' : ''}>2. Escolha a sua senha.</li>
        </ol>

        {stage === 'code' ? (
          <>
            {error && (
              <AuthNotice id={errorId} tone="error">
                {error}
              </AuthNotice>
            )}
            <button
              type="button"
              onClick={() => void askCode()}
              disabled={busy}
              className="inline-flex h-9 w-full items-center justify-center rounded-md bg-accent-strong text-[13px] font-medium text-white transition-colors hover:bg-accent disabled:opacity-50"
            >
              {busy ? 'Enviando…' : 'Enviar código para o meu e-mail'}
            </button>
          </>
        ) : (
          <form
            className="space-y-4"
            onSubmit={(e) => {
              void submit(e);
            }}
            noValidate
          >
            {notice && <AuthNotice tone="success">{notice}</AuthNotice>}
            <AuthField
              label="Código de confirmação"
              htmlFor="code"
              hint="Vale por 15 minutos. Não chegou? Confira o spam."
            >
              <input
                id="code"
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                maxLength={7}
                value={code}
                onChange={(e) => {
                  setCode(e.target.value);
                }}
                aria-describedby={error ? errorId : undefined}
                className={`${authInputClass} font-mono tracking-[0.3em]`}
                placeholder="000 000"
              />
            </AuthField>
            <AuthField
              label="Nova senha"
              htmlFor="new-password"
              hint={`Pelo menos ${String(MIN_LENGTH)} caracteres. Uma frase é mais fácil de lembrar.`}
            >
              <input
                id="new-password"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                }}
                aria-describedby={error ? errorId : undefined}
                className={authInputClass}
              />
            </AuthField>
            <AuthField label="Repita a nova senha" htmlFor="confirm-password">
              <input
                id="confirm-password"
                type="password"
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => {
                  setConfirm(e.target.value);
                }}
                aria-describedby={error ? errorId : undefined}
                className={authInputClass}
              />
            </AuthField>
            {error && (
              <AuthNotice id={errorId} tone="error">
                {error}
              </AuthNotice>
            )}
            <AuthSubmit
              busy={busy}
              disabled={code.replace(/\D/g, '').length !== 6 || !password || !confirm}
              label="Criar meu acesso"
              busyLabel="Criando…"
            />
            <p className="text-center text-[13px]">
              <button
                type="button"
                onClick={() => void askCode()}
                disabled={busy}
                className="text-accent hover:underline disabled:opacity-50"
              >
                Enviar outro código
              </button>
            </p>
          </form>
        )}
      </div>
    </AuthLayout>
  );
}
